<template>
    <div class="multi-filter" :style="{ height: uiHeight !== false ? `${uiHeight}rem` : '100%' }">
        <div class="multi-filter-header c-el-panel-bg">
            <!-- Search Input -->
            <div class="sm-b-s dw-10 f fj-sb">
                <FormSearch class="fg" v-model="qstring" :placeholder="uiSearchPlaceholder" :debounce="false" />
                <slot name="titleActions"></slot>
            </div>
        </div>
        <div class="multi-filter-items">
            <!-- "No matches found" message if no matching items -->
            <div v-if="qstring.length >= 3 && uiItems.length === 0" class="sp-s el--not-found">
                <p>
                    <strong>{{ 'mead.no_matches_found' | athT }}</strong>
                </p>
                <p>{{ 'mead.check_for_typos' | athT }}</p>
            </div>
            <!-- Items -->
            <div v-else>
                <!-- Select All will only show up in following conditions are all met
                     - multi mode
                     - min and max are 0
                     - No query
                     - At least one value to select (only leaf values when allowGroupSelection=false)  -->
                <MultiStepFilterSelectAll v-if="allowSelectAll"
                    :qstring="qstring.length >= 3 ? qstring : ''"
                    :items="uiItems"
                    :engine="engine"
                    @onCheck="onCheck" />
                <MultiFilterItem v-for="item in uiItems"
                    :item="item"
                    :key="item.value + item.uid"
                    :qstring="qstring.length >= 3 ? qstring : ''"
                    :showBadgeCounts="showBadgeCounts"
                    :engine="engine"
                    :startCollapsed="startCollapsed"
                    :showPillsWhenCollapsed="showPillsWhenCollapsed"
                    @onItemSelected="onItemSelected"
                    @onCheck="onCheck" />
            </div>
        </div>
    </div>
</template>
<script>
import FormSearch from '../../atoms/forms/FormSearch.vue';
import MultiStepFilterSelectAll from '../multi-step-filter/MultiStepFilterSelectAll.vue';
import MultiFilterItem from './MultiFilterItem.vue';

export default {
    name: 'mead-multi-filter',
    props: {
        /**
         * Translation key used as the label for what is being searched, shown inside the
         * search input's placeholder text (e.g. "Search options").
         */
        searchPlaceholderLabel: { type: String, default: 'mead.options' },
        /**
         * Public key if using this in a popover
         */
        pubkey: { type: String, default: '' },
        /**
         * Show count of selected children on badges
         */
        showBadgeCounts: { type: Boolean, default: true },
        /**
         * Height of the component
         */
        uiHeight: { type: [String, Number, Boolean], default: '40' },
        /**
         * If the items should start collapsed or not
         */
        startCollapsed: { type: Boolean, default: true },
        /**
         * If the top most list should be sorted
         */
        sortList: { type: Boolean, default: true },
        /**
         * If select all option should be shown
         */
        allowSelectAll: { type: Boolean, default: true},
        /**
         * Show pills when sections are collapsed
         */
        showPillsWhenCollapsed: {type: Boolean, default: false},

        // Below are engine properties

        /**
         * Array of MultiStepFilterItem's
         */
        items: { type: Array, default: () => [] },
        /**
         * Array of values representing the currently selected items. Does not include
         * group values unless includeGroups is true.
         */
        selectedItems: { type: Array, default: () => [] },
        /**
         * Items that are not allowed to be selected because they are restricted.
         * Only for single selection mode
         */
        restrictedItems: { type: Array, default: () => [] },
        /**
         * Max amount of items that can be selected. 0 is no limit
         */
        max: { type: Number, default: 0 },
        /**
         * Minimum amount of items that can be selected. 0 is no limit
         */
        min: { type: Number, default: 0 },
        /**
         * If groups should be included in selection
         */
        includeGroups: { type: Boolean, default: true },
        /**
         * Show Checkmarks on groups or allow clicking groups. If all groups should be
         * clicked set this to true, otherwise use the always_selectable prop for filter item
         */
        allowGroupSelection: { type: Boolean, default: false },
        /**
         * Selection mode
         */
        mode: { type: String, default: () => MeadCommon.MUTLI_STEP_FILTER_MODES.NO_SELECT },
        /**
         * If true, parent groups remain checked even when only some children are selected
         */
        keepPartialGroups: { type: Boolean, default: false },
        /**
         * if true, selection will only include the item selected, not the children of a group.
         */
        isolateSelection: { type: Boolean, default: false },
    },
    components: { MultiFilterItem, FormSearch, MultiStepFilterSelectAll },
    data() {
        const engine = new MeadCommon.MultiFilterEngine()
            .setItems(this.items)
            .setSelectedItems(this.selectedItems)
            .setRestrictedItems(this.restrictedItems)
            .setMax(this.max)
            .setMin(this.min)
            .setIncludeGroups(this.includeGroups)
            .setAllowGroupSelection(this.allowGroupSelection)
            .setMode(this.mode)
            .setKeepPartialGroups(this.keepPartialGroups)
            .setIsolateSelection(this.isolateSelection);
        return {
            qstring: '',
            engine,
        };
    },
    methods: {
        onItemSelected: function (item) {
            if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE) {
                this.engine.setSelectedItems([item.value]);
                this.$emit('onItemSelected', this.engine.selected, item);
            } else if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.MULTI) {
                this.engine.setSelectedItems(item.allValues);
                this.$emit('onItemSelected', this.engine.selected);
            }
            MeadCommon.Flash.publish(`${this.pubkey}:item_selected`, this.engine.selected);
        },
        onCheck: function (selection) {
            const selection_output = this.engine.updateSelection(selection);
            this.$emit('onCheck', selection_output);
            MeadCommon.Flash.publish(`${this.pubkey}:check_changed`, selection_output);
        }
    },
    computed: {
        uiSearchPlaceholder: function () {
            return MeadCommon.I18N.go('mead.search_x', {
                value: MeadCommon.I18N.go(this.searchPlaceholderLabel).toLowerCase()
            });
        },
        uiItems: function () {
            if (this.qstring.length >= 3) {
                const matches = [];

                for (const item of this.items) {
                    matches.push(...item.search(this.qstring));
                }
                return MeadCommon.MultiStepFilterItem.sortWithPinned(matches);
            } else if (this.sortList) {
                return MeadCommon.MultiStepFilterItem.sortWithPinned(this.items);
            } else {
                return this.items;
            }
        },
    },
    watch: {
        items: function () { this.engine.setItems(this.items, false); },
        selectedItems: function () { this.engine.setSelectedItems(this.selectedItems); },
        restrictedItems: function () { this.engine.setRestrictedItems(this.restrictedItems); },
        max: function () { this.engine.setMax(this.max); },
        min: function () { this.engine.setMin(this.min); },
        includeGroups: function () { this.engine.setIncludeGroups(this.includeGroups, false); },
        allowGroupSelection: function () { this.engine.setAllowGroupSelection(this.allowGroupSelection); },
        mode: function () { this.engine.setMode(this.mode); },
        keepPartialGroups: function () { this.engine.setKeepPartialGroups(this.keepPartialGroups, false); },
        isolateSelection: function () { this.engine.setIsolateSelection(this.isolateSelection); }
    },
    created: function () { }
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.multi-filter {
    transition: all .2s ease;

    // Resize instantly
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;

    .multi-filter-header {
        position: absolute;
        width: 100%;
    }

    .multi-filter-items {
        margin-top: 4.5rem;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-gutter: stable;
    }

    .el--not-found {
        color: var(--c-txt-light);
    }

    .multi-filter-items {
        flex: 1;
        overflow: auto;
    }
}
</style>
